import React, { Component } from 'react';
import { Modal } from 'reactstrap';
import '../../assets/css/rfpWizard.scss';
import API from '../../utils/API';

import EventDetailsStep from './rfpWizardSteps/EventDetailsStep';
import LocationsStep from './rfpWizardSteps/LocationsStep';
import PreferredDatesStep from './rfpWizardSteps/PreferredDatesStep';
import MeetingSpaceStep from './rfpWizardSteps/MeetingSpaceStep';
import ConcessionsStep from './rfpWizardSteps/ConcessionsStep';
import KeyConsiderationsStep from './rfpWizardSteps/KeyConsiderationsStep';
import AdditionalServicesStep from './rfpWizardSteps/AdditionalServicesStep';
import ContractSigneeStep from './rfpWizardSteps/ContractSigneeStep';
import ReviewSubmitStep from './rfpWizardSteps/ReviewSubmitStep';

const STEPS = [
	{ id: 'eventDetails', label: 'Event Details' },
	{ id: 'locations', label: 'Locations' },
	{ id: 'preferredDates', label: 'Preferred Dates' },
	{ id: 'meetingSpace', label: 'Meeting Space & Agenda' },
	{ id: 'concessions', label: 'Concessions' },
	{ id: 'keyConsiderations', label: 'Key Considerations' },
	{ id: 'additionalServices', label: 'Additional Services' },
	{ id: 'contractSignee', label: 'Contract Signee' },
	{ id: 'reviewSubmit', label: 'Review & Submit' },
];

// Which steps have required fields, and how to check them
const STEP_REQUIRED = {
	0: (f) => !!f.eventName && !!f.eventType,    // Event Details
	7: (f) => !!f.contractCompanyName,             // Contract Signee
};

class RFPWizardModal extends Component {
	constructor(props) {
		super(props);
		this.contentRef = React.createRef();

		this.state = {
			currentStep: 0,
			visitedSteps: new Set(),
			submitting: false,
			saveStatus: 'All changes saved',
			teamMembers: [],

			// ── Wizard form data ───────────────────────────────
			formData: {
				// Step 1: Event Details
				eventName: '',
				eventType: '',
				expectedPeakAttendees: '',
				maxRoomRate: '',
				hotelStarRating: '',

				// Step 2: Locations
				locations: { regions: [], countries: [], cities: [], hotels: [], locationNotes: '' },

				// Step 3: Preferred Dates
				preferredArrivalDate: '',
				preferredDepartureDate: '',
				alternativeDates: [],
				roomBlock: [],
				programDays: [],
				openToAltArrival: false,
				altArrivalDays: [],
				datePatternNotes: '',

				// Step 4: Meeting Space & Agenda
				agendaItems: [],
				checkedAgendaItems: {},

				// Step 5: Concessions
				customConcessions: [],
				selectedAutoItems: [],

				// Step 6: Key Considerations
				decisionFactors: '',
				keyConsiderations: '',
				eventHistory: '',
				reservationNotes: '',

				// Step 7: Additional Services
				additionalServices: [],

				// Step 8: Contract Signee
				contractCompanyName: '',
				contractContactName: '',
				contractEmail: '',
				contractPhone: '',
				contractSignerLocation: '',

				// Team Members notification
				notifyTeamMembers: [],
			}
		};
	}

	async componentDidMount() {
		const { orgId } = this.props;
		if (!orgId) return;
		try {
			const res = await API().get(`Organizations/${orgId}/members`);
			if (res.data && res.data.members) {
				this.setState({ teamMembers: res.data.members });
			}
		} catch (e) {
			console.error('Failed to load team members:', e);
		}
	}

	// Returns a map of stepIndex -> true (valid) / false (has missing required fields)
	getStepValidity = () => {
		const { formData } = this.state;
		const validity = {};
		STEPS.forEach((_, index) => {
			const check = STEP_REQUIRED[index];
			validity[index] = check ? check(formData) : true;
		});
		return validity;
	};

	isFormValid = () => {
		const validity = this.getStepValidity();
		return Object.values(validity).every(Boolean);
	};

	handleFieldChange = (field, value) => {
		this.setState(prev => ({
			formData: { ...prev.formData, [field]: value },
			saveStatus: 'Saving...'
		}), () => {
			clearTimeout(this._saveTimer);
			this._saveTimer = setTimeout(() => {
				this.setState({ saveStatus: 'All changes saved' });
			}, 800);
		});
	};

	goToStep = (index) => {
		this.setState(prev => {
			const visited = new Set(prev.visitedSteps);
			visited.add(prev.currentStep); // mark the step we're leaving as visited
			return { currentStep: index, visitedSteps: visited };
		}, () => {
			if (this.contentRef.current) {
				this.contentRef.current.scrollTop = 0;
			}
		});
	};

	handleNext = () => {
		const { currentStep } = this.state;
		if (currentStep < STEPS.length - 1) {
			this.goToStep(currentStep + 1);
		}
	};

	handleBack = () => {
		const { currentStep } = this.state;
		if (currentStep > 0) {
			this.goToStep(currentStep - 1);
		}
	};

	handleSubmit = async () => {
		const { formData } = this.state;
		const { onSubmit } = this.props;
		const combinedConcessions = Array.from(new Set([
			...(formData.selectedAutoItems || []),
			...(formData.customConcessions || [])
		]));

		this.setState({ submitting: true });
		try {
			const requestData = {
				name: formData.eventName || 'New Proposal Request',
				eventDetails: {
					eventName: formData.eventName,
					eventType: formData.eventType || 'Other',
					expectedPeakAttendees: parseInt(formData.expectedPeakAttendees) || 0,
					maxRoomRate: parseFloat(formData.maxRoomRate) || 0,
					hotelStarRating: formData.hotelStarRating,
					arrivalDate: formData.preferredArrivalDate || null,
					departureDate: formData.preferredDepartureDate || null,
					alternateDates: (formData.alternativeDates || []).map(d => ({
						arrivalDate: d.arrivalDate || null,
						departureDate: d.departureDate || null
					})),
					programDays: formData.programDays,
					openToAltArrival: formData.openToAltArrival,
					altArrivalDays: formData.altArrivalDays,
					datePatternNotes: formData.datePatternNotes,
					locations: formData.locations
				},
				roomBlock: {
					datesFlexible: false,
					flexibilityNotes: '',
					dailyGrid: (formData.roomBlock || []).map(rb => ({
						date: rb.date || null,
						king: parseInt(rb.king) || 0,
						double: parseInt(rb.double) || 0,
						suite: parseInt(rb.suite) || 0,
						staff: parseInt(rb.staff) || 0,
						total: parseInt(rb.total) || 0,
						notes: ''
					}))
				},
				agendaItems: (formData.agendaItems || []).map(item => ({
					roomType: item.roomType || '',
					startTime: item.startTime || '',
					endTime: item.endTime || '',
					attendees: parseInt(item.attendees) || 0,
					selectedDates: item.selectedDates || [],
					require24Hour: item.require24Hour || false,
					setupPreference: item.setupPreference || [],
					sessionName: item.sessionName || '',
					roomSize: parseInt(item.roomSize) || 0,
					roomSizeUnit: item.roomSizeUnit || 'sq. ft.',
					copiesPerDay: parseInt(item.copiesPerDay) || 1,
					notes: item.notes || ''
				})),
				concessions: {
					custom: combinedConcessions
				},
				keyConsiderations: {
					decisionFactors: formData.decisionFactors || '',
					keyConsiderations: formData.keyConsiderations || '',
					eventHistory: formData.eventHistory || '',
					reservationNotes: formData.reservationNotes || ''
				},
				additionalServices: formData.additionalServices || [],
				plannerInfo: {
					firstName: formData.contractContactName ? formData.contractContactName.split(' ')[0] : '',
					lastName: formData.contractContactName ? formData.contractContactName.split(' ').slice(1).join(' ') : '',
					contactName: formData.contractContactName || '',
					companyName: formData.contractCompanyName || '',
					phone: formData.contractPhone || '',
					email: formData.contractEmail || '',
					signerLocation: formData.contractSignerLocation || '',
					address: {
						country: 'US',
						state: '',
						city: '',
						zipCode: ''
					}
				},
				agencyInfo: {
					isCommissionable: false,
					agencyName: '',
					iataNumber: '',
					address: {
						country: 'US',
						state: '',
						city: '',
						zipCode: ''
					}
				},
				additionalInfo: {
					responseDueDate: null,
					decisionDate: null,
					requestedAmenities: [],
					eventDescription: '',
					decisionFactors: formData.decisionFactors || '',
					budgetDirection: ''
				},
				notifyTeamMembers: formData.notifyTeamMembers || [],
			};

			await onSubmit(requestData);
			this.setState({ submitting: false });
		} catch (error) {
			console.error('Error submitting RFP:', error);
			this.setState({ submitting: false });
		}
	};

	renderStep = () => {
		const { currentStep, formData, teamMembers } = this.state;
		const stepProps = { data: formData, onChange: this.handleFieldChange };

		switch (currentStep) {
			case 0: return <EventDetailsStep {...stepProps} />;
			case 1: return <LocationsStep {...stepProps} />;
			case 2: return <PreferredDatesStep {...stepProps} />;
			case 3: return <MeetingSpaceStep {...stepProps} />;
			case 4: return <ConcessionsStep {...stepProps} />;
			case 5: return <KeyConsiderationsStep {...stepProps} />;
			case 6: return <AdditionalServicesStep {...stepProps} />;
			case 7: return <ContractSigneeStep {...stepProps} teamMembers={teamMembers} />;
			case 8: return <ReviewSubmitStep {...stepProps} onGoToStep={this.goToStep} teamMembers={teamMembers} />;
			default: return null;
		}
	};

	render() {
		const { isOpen, toggle } = this.props;
		const { currentStep, saveStatus, submitting, visitedSteps } = this.state;
		const isLastStep = currentStep === STEPS.length - 1;
		const isFirstStep = currentStep === 0;
		const eventName = this.state.formData.eventName || 'New Proposal Request';
		const stepValidity = this.getStepValidity();
		const formValid = this.isFormValid();

		return (
			<Modal
				isOpen={isOpen}
				toggle={toggle}
				className="rfpWizardModal"
				modalClassName="rfpWizardModalWrapper"
				fullscreen
				fade={false}
			>
				<div className="rfp-wizard-root">

					{/* ── Top header bar ─────────────────────────────── */}
					<div className="rfp-wizard-header">
						<div className="rfp-header-left">
							<button className="rfp-header-exit-btn" onClick={toggle}>
								<i className="las la-arrow-left" />
								Exit
							</button>
						</div>

						<div className="rfp-header-title">
							<i className="las la-file-alt" />
							{eventName}
						</div>

						<div className="rfp-header-right">
							{/* <span className="rfp-header-save-status">
								{saveStatus === 'Saving...'
									? <><i className="las la-sync-alt" /> Saving</>
									: saveStatus
								}
							</span> */}
							{isLastStep && (
								<button
									className="rfp-header-submit-btn"
									onClick={this.handleSubmit}
									disabled={submitting || !formValid}
									title={!formValid ? 'Please fill in all required fields before submitting' : ''}
								>
									<i className="las la-paper-plane" />
									{submitting ? 'Submitting...' : 'Submit RFP'}
								</button>
							)}
						</div>
					</div>

					{/* ── Body: sidebar + content ─────────────────────── */}
					<div className="rfp-wizard-body" ref={this.contentRef}>

						{/* Left sidebar nav */}
						<div className="rfp-wizard-sidebar">
							{STEPS.map((step, index) => {
								const hasRequired = STEP_REQUIRED[index] !== undefined;
								const isValid = stepValidity[index];
								const isActive = currentStep === index;
								return (
									<button
										key={step.id}
										className={`rfp-sidebar-step ${isActive ? 'active' : ''}`}
										onClick={() => this.goToStep(index)}
									>
										<span className="rfp-sidebar-step-label">{step.label}</span>
										{hasRequired && !isValid && visitedSteps.has(index) && (
											<span className="rfp-sidebar-required-dot" title="Required fields missing" />
										)}
									</button>
								);
							})}
						</div>
                        <div className="rfp-wizard-content-wrapper">
                            {/* Main scrollable content */}
                            <div className="rfp-wizard-content">
                                {this.renderStep()}
                                
                            </div>

                            {/* ── Footer navigation ─────────────────────────── */}
                            <div className="rfp-wizard-footer">
                                <div className="rfp-footer-left">
                                    {isFirstStep ? (
                                        <button className="rfp-footer-cancel-btn" onClick={toggle}>
                                            Cancel
                                        </button>
                                    ) : (
                                        <button className="rfp-footer-back-btn" onClick={this.handleBack}>
                                            <i className="las la-angle-left" />
                                            {STEPS[currentStep - 1]?.label}
                                        </button>
                                    )}
                                </div>

                                <div className="rfp-footer-right">
                                    {isLastStep ? (
                                        <button
                                            className="rfp-footer-submit-nav-btn"
                                            onClick={this.handleSubmit}
                                            disabled={submitting || !formValid}
                                            title={!formValid ? 'Please fill in all required fields before submitting' : ''}
                                        >
                                            {submitting ? 'Submitting...' : 'Submit RFP'}
                                            <i className="las la-angle-right" />
                                        </button>
                                    ) : (
                                        <button className="rfp-footer-next-btn" onClick={this.handleNext}>
                                            {STEPS[currentStep + 1]?.label}
                                            <i className="las la-angle-right" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        
					</div>

				</div>
			</Modal>
		);
	}
}

export default RFPWizardModal;
